import { STATUS_CODE } from '@kc-console/core';

import { getRequestUrlData } from '../../utils/getRequestUrlData';
import { getSupportedBrowserLanguages } from '../../utils/getSupportedBrowserLanguages';
import { parsePathname } from '../../utils/parsePathname';
import { syncI18nResources } from '../../utils/syncI18nResources';

import type { BaseState, ServiceAppState } from '@kc-console/context';
import type { Guard } from '../../types';

/**
 * 토큰 없이 브라우저 선호 언어 기반 i18n 리소스를 사전 로드하는 가드
 * @description
 * - 인증 이전 단계에서 Accept-Language와 콘솔 지원 언어의 교집합을 preload합니다
 * - 후속 determineI18nResourceGuard에서 사용자 언어 기반으로 ssrConfig를 덮어씁니다
 * - 비차단(non-blocking): 실패 시에도 가드 체인을 중단하지 않습니다
 */
export const preloadBrowserI18nResourceGuard: Guard<ServiceAppState & BaseState> = async ({
  context,
  queryClient,
}) => {
  const { urlObj } = getRequestUrlData(context);
  const { serviceKeyword } = parsePathname(urlObj.pathname);

  if (!serviceKeyword) {
    return { status: STATUS_CODE.OK };
  }

  const languages = getSupportedBrowserLanguages(context.req?.headers?.['accept-language']);

  try {
    const ssrConfig = await syncI18nResources({
      context,
      namespace: serviceKeyword,
      queryClient,
      languages,
      locale: languages[0],
      guard: preloadBrowserI18nResourceGuard.displayName,
    });

    return { ssrConfig, status: STATUS_CODE.OK };
  } catch (error) {
    console.warn('[i18n PRELOAD] Failed to preload translations:', error);
    return { status: STATUS_CODE.OK };
  }
};

preloadBrowserI18nResourceGuard.displayName = 'preloadBrowserI18nResourceGuard';
